import React, { useMemo } from "react";
import { datetime } from "../../../../util/library/datetime";
import { useSelector } from '../../../../util/store/store';
import { EXECUTION_TYPE, InfoMode } from "../../../../util/varibles/constants";
import { IOperation2, IRoundPlan, ITreatment2 } from "../../../../util/varibles/interface";
import { RoundMapProvider } from "../../../PlanOperationPage/Popup/EditOp/EditOperationContext/RoundMapContext";
import Round from "../../../PlanOperationPage/Popup/EditOp/Treatment/RoundMap/Round";
import Tank from "../../../PlanOperationPage/Popup/EditOp/Treatment/RoundMap/Tank";
import { selectOpDetail } from '../../constants';
import styles from "../style.module.scss";

const Arrived = ({ time, format }: { time?: number, format: string }) => {
    return <div className={styles.step}>
        <div className={styles.arrowDown} />
        <div className="flex flex-col items-center py-5">
            <div className="text-[14px]">{time ? datetime(time).format(format) : "-"}</div>
            <div className="text-[18px] leading-[20px]">Arrived at site</div>
        </div>
        <div className={styles.arrowDown} />
    </div>
}

const Completed = ({ time, format }: { time?: number, format: string }) => {
    return <div className={styles.step}>
        <div className={styles.arrowDown} />
        <div className="flex flex-col items-center py-5">
            <div className="text-[14px]">{time ? datetime(time).format(format) : "-"}</div>
            <div className="text-[18px] leading-[20px]">Completed</div>
        </div>
    </div>
}

// const MoveBetweenRounds = ({ data = [], format = SHORT_DATETIME_FORMAT }: { data: IRoundPlan['vessel_movements'], format?: string }) => {
//     if (data.length === 0)
//         return null;
//     const names: Record<string, string> = {
//         departure: 'Departure',
//         arrive: 'Arrived at site',
//     };

//     return <div className={styles.step}>
//         <div className={styles.arrowDown} />
//         <div className="pt-[18px] pb-[20px]">
//             {data.map((item, index) => <React.Fragment key={item.start_time}>
//                 <div key={item.start_time} className="flex flex-col items-center">
//                     <span className="text-[14px]">{datetime(item.start_time).format(format)}</span>
//                     <span className="text-[18px]">{names[item.type]}</span>
//                 </div>
//                 {index < data.length - 1 && (
//                     <div className="mt-1 mb-2 text-[20px] leading-none text-center">
//                         -----
//                     </div>
//                 )}
//             </React.Fragment>)}
//         </div>
//         <div className={styles.arrowDown} />
//     </div>
// }

interface IProps {
    data: IOperation2<ITreatment2>
}

const ActivityLog: React.FC<IProps> = ({ data }) => {
    const { activity_log } = data;
    const { round_map, real_round_map, execution_type } = data.operation;
    const infoMode = useSelector(selectOpDetail.infoMode);

    const items = useMemo(() => {
        if (!round_map)
            return [];

        const rs = [];
        const start = datetime(activity_log[0].est_start_time);
        const finish = datetime(activity_log[activity_log.length - 1].est_start_time)
        const format = start.date !== finish.date ? 'DD/MM HH:mm' : "HH:mm";
        rs.push({
            label: 'PLAN ACTIVITIES LOG',
            key: '1',
            children: <div className="flex flex-col">
                <Arrived time={start.time} format={format} />
                <RoundMapProvider format={format}>
                    {Object.keys(round_map).map((key, i, arr) => {
                        const { after_processes, tanks } = round_map[key];

                        return <Round
                            key={key}
                            roundId={key}
                            after_processes={after_processes}
                        >
                            <div className='grid gap-3 z-[1]'
                                style={{ gridTemplateColumns: `repeat(${tanks.length}, 1fr)` }}>
                                {tanks.map((item, i: number) => <Tank key={i} data={item} />)}
                            </div>
                        </Round>
                    })}
                </RoundMapProvider>
                <Completed time={finish.time} format={format} />
            </div>,
        })
        if (execution_type === EXECUTION_TYPE.AUTO) {
            const start = activity_log[0].arrive_time || activity_log[0].real_start_time;
            const end = activity_log[activity_log.length - 1].depature_time || activity_log[activity_log.length - 1].real_start_time;

            if (start || end || real_round_map) {
                const format = (start && finish && datetime(start).date !== datetime(finish).date) ? "DD/MM HH:mm" : "HH:mm";
                rs.push({
                    label: 'ACTUAL ACTIVITIES LOG',
                    key: '2',
                    children: <div className="flex flex-col">
                        {start && <Arrived time={start} format={format} />}
                        {!!real_round_map && <RoundMapProvider format={format}>
                            {Object.keys(real_round_map).flatMap((key, i) => {
                                const { tanks } = real_round_map[key];
                                return [
                                    <Round key={key} roundId={key} after_processes={[]}>
                                        <div className='grid gap-3 z-[1]'
                                            style={{ gridTemplateColumns: `repeat(${tanks.length}, 1fr)` }}>
                                            {tanks.map((item: any, i: number) => <Tank key={i} data={item} />)}
                                        </div>
                                    </Round>
                                ]
                            })}
                        </RoundMapProvider>
                        }
                        {end && <Completed time={end} format={format} />}
                    </div >,
                })
            }
        } else {
            const sortRoundsByTime = Object.keys(round_map).sort((a, b) => {
                const timeA = round_map[a].real_start_time;
                const timeB = round_map[b].real_start_time;

                if (!timeA && !timeB) return 0;
                if (!timeA) return 1;
                if (!timeB) return -1;

                return timeA - timeB;
            });
            let tanksObject: { [round: string]: IRoundPlan['tanks'] } = {}, time: { [tankId: string]: number | undefined } = {};
            const count = sortRoundsByTime.length - 1;
            for (let roundIndex = count; roundIndex >= 0; roundIndex--) {
                const roundKey = sortRoundsByTime[roundIndex];
                const { tanks } = round_map[roundKey] as IRoundPlan;
                const newTanks: IRoundPlan['tanks'] = [];

                for (let tankIndex = 0; tankIndex < tanks.length; tankIndex++) {
                    const tank = tanks[tankIndex];
                    const { processes = [], tank: tankId } = tank;
                    const newProcesses: typeof processes = [];

                    for (let processIndex = processes.length - 1; processIndex >= 0; processIndex--) {
                        const process = processes[processIndex];
                        const { real_start_time } = process;

                        newProcesses.unshift({
                            ...process,
                            start_time: real_start_time || 0,
                            finish_time: time[tankId] || 0,
                        });

                        time[tankId] = real_start_time;
                    }
                    newTanks.push({ ...tank, processes: newProcesses, });
                }
                tanksObject[roundKey] = newTanks;
            }
            const start = activity_log[0].arrive_time || activity_log[0].real_start_time;
            const finish = activity_log[activity_log.length - 1].depature_time || activity_log[activity_log.length - 1].real_start_time
            const format = (start && finish && datetime(start).date !== datetime(finish).date) ? "DD/MM HH:mm" : "HH:mm";
            rs.push({
                label: 'ACTUAL ACTIVITIES LOG',
                key: '2',
                children: <div className="flex flex-col">
                    <Arrived time={start} format={format} />
                    <RoundMapProvider>
                        {sortRoundsByTime.map((key) => {
                            const tanks = tanksObject[key];

                            return <Round key={key} roundId={key} after_processes={[]}>
                                <div className='grid gap-3 z-[1]' style={{ gridTemplateColumns: `repeat(${tanks.length}, 1fr)` }}>
                                    {tanks.map((item: any, i: number) => <Tank key={i} data={item} />)}
                                </div>
                            </Round>
                        })}
                    </RoundMapProvider>
                    <Completed time={finish} format={format} />
                </div>,
            })
        }

        return rs;
    }, [round_map, execution_type, real_round_map, activity_log]);

    const activeKey = infoMode === InfoMode.PLAN ? '1' : '2';

    return <>{items.find(item => item.key === activeKey)?.children}</>;
}

export default ActivityLog
